import { useModalStore } from "@/hooks/Modal/hooks";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { Badge, Button, Popover, Radio, RadioChangeEvent, Modal, Card, List, Space, Typography } from "antd";
import { observer } from "mobx-react-lite";
import React from "react";
import { useHistory } from "react-router";
import { InvisiblePlatformOrderResult } from "@/services/api/order";
import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import OrderStatusNameMap from "@/map/OrderStatusNameMap";
import OrderStatus from "@/enum/OrderStatus";

interface RefundOrderSelectProps {
    orderTab: OrderTab;

    shopId: number;
}

const SummaryModal: React.FC<{ result: InvisiblePlatformOrderResult } & InjectBaseModalProps> = function (props) {
    const { open, onCancel, result } = props;

    return (
        <Modal
            zIndex={9999}
            open={open}
            onCancel={onCancel}
            title={
                <Typography.Title level={5} style={{ margin: 0 }}>
                    拆分订单详情
                </Typography.Title>
            }
            width={720}
            footer={
                <div style={{ textAlign: "right" }}>
                    <Button type="primary" onClick={onCancel}>
                        关闭
                    </Button>
                </div>
            }
        >
            <Space orientation="vertical" size={20} style={{ width: "100%" }}>
                {result.orders.map((summary, index) => {
                    const { platformOrder, appVisibleOrders = [] } = summary;

                    if (!platformOrder?.originalId) return null;

                    return (
                        <Card
                            key={index}
                            size="small"
                            style={{
                                borderRadius: 8,
                                border: "1px solid #f0f0f0",
                            }}
                            styles={{ body: { padding: 16 } }}
                            title={
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <Typography.Text strong>平台订单：{platformOrder.originalId}</Typography.Text>

                                    <span>{OrderStatusNameMap[platformOrder.status]}</span>
                                </div>
                            }
                        >
                            <List
                                size="small"
                                dataSource={appVisibleOrders}
                                locale={{ emptyText: "暂无子订单" }}
                                renderItem={(item) => (
                                    <List.Item
                                        style={{
                                            padding: "8px 0",
                                            borderBottom: "1px dashed #f0f0f0",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                width: "100%",
                                            }}
                                        >
                                            <Typography.Text>{item.originalId}</Typography.Text>

                                            <span>
                                                {item.isRefunding
                                                    ? item.status === OrderStatus.PENDING_SHIPMENT
                                                        ? "待发货退款中"
                                                        : "已发货退款中"
                                                    : OrderStatusNameMap[item.status]}
                                            </span>
                                        </div>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    );
                })}
            </Space>
        </Modal>
    );
};

const RefundOrderSelect: React.FC<RefundOrderSelectProps> = function (props) {
    const { orderTab, shopId } = props;

    const orderManagementStore = useOrderManagementStore();
    const { table, invisiblePlatformOrderResult } = orderManagementStore;
    const { totalPendingShipmentRefundingOrders, totalShippedRefundingOrders } =
        orderManagementStore?.orderListCount || {};

    const modalStore = useModalStore();
    const history = useHistory();

    if (![OrderTab.PENDING_SHIPMENT_REFUNDING, OrderTab.SHIPPED_REFUNDING].includes(orderTab)) return null;

    function handleChange(e: RadioChangeEvent) {
        const newTab = e.target.value;

        const path = `/order/list?shopId=${shopId}&tab=${newTab}`;

        history.push(path);
    }

    function buildBadge(tab: OrderTab) {
        if (tab === OrderTab.PENDING_SHIPMENT_REFUNDING) {
            const el = (
                <Badge
                    count={totalPendingShipmentRefundingOrders}
                    style={{ backgroundColor: "#faad14" }}
                    overflowCount={Number.MAX_SAFE_INTEGER}
                    className="ml5"
                />
            );

            if (table.orders?.length) return el;
            if (!invisiblePlatformOrderResult?.totalCount) return el;
            if (shopId !== invisiblePlatformOrderResult.shopId || tab !== invisiblePlatformOrderResult.tab) return el;

            return (
                <Popover
                    open
                    content={
                        <div className="fb">
                            <span>
                                有{invisiblePlatformOrderResult.totalCount}个平台订单已拆分，实际订单在对应状态Tab中
                            </span>
                            <Button
                                type="link"
                                className="ml3"
                                onClick={() => {
                                    modalStore.openModal(SummaryModal, {
                                        result: invisiblePlatformOrderResult,
                                    });
                                }}
                            >
                                查看详情
                            </Button>
                        </div>
                    }
                >
                    {el}
                </Popover>
            );
        } else if (tab === OrderTab.SHIPPED_REFUNDING) {
            const el = (
                <Badge
                    count={totalShippedRefundingOrders}
                    style={{ backgroundColor: "#faad14" }}
                    overflowCount={Number.MAX_SAFE_INTEGER}
                    className="ml5"
                />
            );

            if (table.orders?.length) return el;
            if (!invisiblePlatformOrderResult?.totalCount) return el;
            if (shopId !== invisiblePlatformOrderResult.shopId || tab !== invisiblePlatformOrderResult.tab) return el;

            return (
                <Popover
                    open
                    content={
                        <div className="fb">
                            <span>
                                有{invisiblePlatformOrderResult.totalCount}个平台订单已拆分，实际订单在对应状态Tab中
                            </span>
                            <Button
                                type="link"
                                className="ml3"
                                onClick={() => {
                                    modalStore.openModal(SummaryModal, {
                                        result: invisiblePlatformOrderResult,
                                    });
                                }}
                            >
                                查看详情
                            </Button>
                        </div>
                    }
                >
                    {el}
                </Popover>
            );
        }
    }

    return (
        <div className="mr130 mb10">
            <Radio.Group buttonStyle="solid" value={orderTab} onChange={handleChange}>
                <Radio.Button value={OrderTab.PENDING_SHIPMENT_REFUNDING}>
                    待发货退款订单
                    {buildBadge(OrderTab.PENDING_SHIPMENT_REFUNDING)}
                </Radio.Button>

                <Radio.Button value={OrderTab.SHIPPED_REFUNDING}>
                    已发货退款订单
                    {buildBadge(OrderTab.SHIPPED_REFUNDING)}
                </Radio.Button>
            </Radio.Group>
        </div>
    );
};

export default observer(RefundOrderSelect);
